import { types, flow, getSnapshot, getPath, destroy } from "mobx-state-tree";
import { isEqual, isArray } from "underscore";

import { getPatchProps, filterUndefinedProps } from "util/mst-utils";
import { getCampaigns, createNewCampaign } from "api/campaigns";

import BaseBackboneCollectionMigrationStore from "pages/common/state/store/backbone-collection-template";

import { CampaignStore } from "./campaign-store";

const model = {
    campaigns: types.optional(types.array(CampaignStore), []),
    selectedCampaign: types.maybe(types.reference(CampaignStore)),
    isLoading: true
};

const volatile = () => ({});

const views = (self) => ({
    get length() {
        return self.campaigns.length;
    },

    get selectedCampaignId() {
        return self?.selectedCampaign?.campaignId;
    },

    getAsRawObjects() {
        return self.campaigns.map((campaign) => getSnapshot(campaign));
    },

    getById(id) {
        if (!id) {
            return;
        }
        return self.campaigns.find((campaign) => {
            if (id?.includes(`:`)) {
                return campaign.compoundId === id;
            }
            return campaign.campaignId === id;
        });
    },

    getId(item) {
        if (item.contextId) {
            return item.contextId.split(`:`)[1];
        }
        return (
            item.galleryId ||
            item.channelId ||
            item.brandId ||
            item.eventId ||
            undefined
        );
    },

    getByCompoundId(compoundId) {
        return self.campaigns.find(
            (campaign) => campaign.compoundId === compoundId
        );
    },

    getByPatchId(id) {
        return self.campaigns.find((campaign) => {
            const path = getPath(campaign);
            const parts = path.split(`/`);
            const patchId = isArray(parts) ? parts[parts.length - 1] : ``;
            return patchId === id;
        });
    },

    getModelIdFromPatch(patch) {
        const props = getPatchProps(patch);
        const campaign = self.getByPatchId(props.patchId);
        return campaign?.campaignId;
    },

    getActiveCampaigns() {
        return self.campaigns.filter((campaign) => campaign.isActive);
    },

    getArchivedCampaigns() {
        return self.campaigns.filter((campaign) => campaign.isArchived);
    },

    getDisplayNames() {
        return self.campaigns.map((campaign) => campaign.getDisplayName());
    },

    campaignExists(campaignId) {
        return self.getById(campaignId) !== undefined;
    }
});

const actions = (self) => ({
    /**
     * @summary Resets entire campaigns list with new CampaignStore models
     * @param {Array} campaigns
     */
    resetCampaigns(campaigns) {
        const sortedCampaigns = self.sortByName(campaigns);
        self.campaigns.replace(sortedCampaigns);
    },

    /**
     * @summary Updates the model of a specific campaign
     * @param {CampaignObject} data
     */
    resetCampaign(data) {
        const id = self.getId(data);
        const campaign = self.getById(id);
        if (campaign) {
            campaign.reset(data);
        } else {
            self.addCampaign(data);
        }
    },

    /**
     * @summary Adds/updates multiple campaigns to the store
     * @param {Array} data - list of campaign objects
     * @return {Array} Returns the updated campaigns list
     */
    addCampaigns(data, options = { delete: false }) {
        if (options?.delete) {
            const ids = data.map((item) => self.getId(item));
            const deletedCampaigns = self.campaigns.filter(
                (campaign) => !ids.includes(campaign.campaignId)
            );
            deletedCampaigns.forEach((campaign) =>
                self.removeCampaign(campaign)
            );
        }

        data.forEach((item) => {
            const id = self.getId(item);
            const existingCampaign = self.getById(id);
            if (existingCampaign) {
                existingCampaign.refresh(item);
            } else {
                self.addCampaign(item);
            }
        });
        return self.campaigns;
    },

    /**
     * @summary Add a new campaign to the campaigns list (in the correct sort position)
     * If the campaign already exists, then the existing campaign is updated
     * @param {Object} data
     */
    addCampaign(data) {
        const id = self.getId(data);
        if (self.campaignExists(id)) {
            self.resetCampaign(data);
        } else {
            const sortedData = self.sortByName([
                ...self.getAsRawObjects(),
                data
            ]);
            const itemPos = sortedData.findIndex(
                (item) => self.getId(item) === id
            );
            const campaign = CampaignStore.create(data);
            self.campaigns.splice(itemPos, 0, campaign);
        }
    },

    /**
     * @summary Remove a campaign from the campaigns list
     * @param {CampaignStore | String} campaign
     */
    removeCampaign(campaign) {
        let node = campaign;
        if (typeof campaign === `string`) {
            node = self.getById(campaign);
        }
        destroy(node);
    },

    /**
     * @summary Sets the parsed CampaignStore as the selectedCampaign
     * If a string value is parsed, then the selectedCampaign is set with setSelectedCampaignById()
     * If a falsy value is parsed, then the selectedCampaign is set to undefined
     * @param {CampaignStore | String} campaign
     */
    setSelectedCampaign(campaign) {
        if (typeof campaign === `string`) {
            self.setSelectedCampaignById(campaign);
        } else {
            self.selectedCampaign = campaign ?? undefined;
        }
    },

    /**
     * @summary Gets the CampaignStore object from the campaigns store and sets it as the selectedCampaign
     * If a falsy value is parsed, then the selectedCampaign is set to undefined
     * @param {String} id
     */
    setSelectedCampaignById(id) {
        if (id) {
            const campaign = self.getById(id);
            self.setSelectedCampaign(campaign);
        } else {
            self.selectedCampaign = undefined;
        }
    },

    sortByName(campaigns) {
        return campaigns.sort((c1, c2) => {
            return `${c1.name}`.localeCompare(c2.name);
        });
    },

    sort() {
        self.campaigns.replace(
            [...self.campaigns].sort((c1, c2) => {
                return `${c1.getName()}`.localeCompare(c2.getName());
            })
        );
    },

    hasChanged(data) {
        const campaign = self.getById(data?.campaignId);
        const snapshot = filterUndefinedProps(getSnapshot(campaign));
        return !isEqual(snapshot, data);
    }
});

const asyncActions = (self) => ({
    /**
     * @summary Make an api call to fetch all campaigns
     * @async
     * @return {CampaignObject | Error}
     */
    fetch: flow(function* fetch() {
        self.isLoading = true;
        try {
            const { data } = yield getCampaigns();
            const campaigns = data.map((item) => {
                const type = item.contextId.split(`:`)[0];
                return {
                    ...item[type],
                    contextId: item.contextId,
                    type
                };
            });
            self.addCampaigns(campaigns, {
                delete: true
            });
            return self.getAsRawObjects();
        } catch (error) {
            console.error(error);
            return error;
        } finally {
            self.isLoading = false;
        }
    }),

    /**
     * @summary Make an api call to create a new campaign
     * @async
     * @param {CampaignObject} newData
     * @return {CampaignObject | Error}
     */
    createCampaign: flow(function* createCampaign(newData) {
        try {
            const { data } = yield createNewCampaign(newData);
            self.addCampaign(data);
            return self.getAsRawObjects();
        } catch (error) {
            console.error(error);
            return error;
        }
    })
});

const backboneUtilityActions = () => ({
    __comparator(model1, model2) {
        return `${model1.get(`displayName`)}`.localeCompare(
            model2.get(`displayName`)
        );
    }
});

const _CampaignsStore = types
    .model(`Campaigns`, model)
    .volatile(volatile)
    .views(views)
    .actions(actions)
    .actions(asyncActions)
    .actions(backboneUtilityActions);

export const CampaignsStore = BaseBackboneCollectionMigrationStore(
    _CampaignsStore,
    CampaignStore,
    `campaigns`
);
